import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Navbar from '../components/Navbar';
import { MapPin, Clock, Users, Award, User, Calendar, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { parseLocalDate, formatTimeRange } from '../utils/dateTime';

const OpportunityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOpportunity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/opportunities/${id}`);
      setOpportunity(response.data);
    } catch (error) {
      toast.error('Failed to load opportunity');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  const handleSignup = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/signups/${id}`);
      toast.success('Successfully signed up!');
      fetchOpportunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/signups/${id}`);
      toast.success('Signup cancelled');
      fetchOpportunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestHours = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/hours/request`, { opportunity_id: id });
      toast.success('Hours requested successfully!');
      navigate('/my-hours');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request hours');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!opportunity) return null;

  const isFull = opportunity.capacity > 0 && opportunity.signups_count >= opportunity.capacity;
  const isClosed = opportunity.status === 'closed';
  const spotsLeft = opportunity.capacity > 0 ? opportunity.capacity - opportunity.signups_count : null;
  const isUnlimited = !opportunity.capacity || opportunity.capacity === 0;
  const oppDate = parseLocalDate(opportunity.date);
  const formattedDate = format(oppDate, 'EEEE, MMMM d, yyyy');
  const isPastDate = oppDate < new Date();
  const userSignupStatus = opportunity.user_signup_status;

  return (
    <div className="min-h-screen" data-testid="opportunity-details-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#8B2E3C] hover:underline mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to opportunities
        </button>

        <div className="card">
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] mb-4" data-testid="opportunity-title">
              {opportunity.title}
            </h1>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Date</div>
                  <div className="font-medium" data-testid="opportunity-date">{formattedDate}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Time</div>
                  <div className="font-medium" data-testid="opportunity-time">
                    {formatTimeRange(opportunity.start_time, opportunity.end_time)}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Location</div>
                  <div className="font-medium" data-testid="opportunity-location">{opportunity.location}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Award className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Credit Hours</div>
                  <div className="font-medium" data-testid="opportunity-credit">{opportunity.credit_hours} hours</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Capacity</div>
                  <div className="font-medium" data-testid="opportunity-capacity">
                    {isUnlimited ? (
                      <>{opportunity.signups_count} signed up <span className="text-[#888] text-sm">(no limit)</span></>
                    ) : (
                      <>
                        {opportunity.signups_count} / {opportunity.capacity}
                        {isFull && <span className="ml-2 inline-block bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full" data-testid="full-badge">Full</span>}
                        {!isFull && spotsLeft !== null && spotsLeft <= 3 && spotsLeft > 0 && (
                          <span className="text-[#C9A227] text-sm ml-2">({spotsLeft} left)</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-[#8B2E3C] mt-0.5" />
                <div>
                  <div className="text-sm text-[#666666]">Host Contact</div>
                  <div className="font-medium" data-testid="opportunity-host">{opportunity.host_contact}</div>
                </div>
              </div>
            </div>

            <div className="border-t border-[#E9DDCF] pt-6">
              <h3 className="text-lg font-bold mb-2">Description</h3>
              <p className="text-[#444444] leading-relaxed" data-testid="opportunity-description">
                {opportunity.description}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            {isClosed && (
              <div className="flex-1 text-center py-3 bg-red-50 border-2 border-red-200 rounded-lg" data-testid="closed-message">
                <p className="font-medium text-red-700">This opportunity is closed</p>
              </div>
            )}
            {!isClosed && !opportunity.user_signed_up && !isFull && (
              <button
                onClick={handleSignup}
                className="btn-primary flex-1"
                disabled={actionLoading}
                data-testid="signup-button"
              >
                {actionLoading ? 'Signing up...' : 'Sign Up'}
              </button>
            )}
            {!isClosed && !opportunity.user_signed_up && isFull && (
              <div className="flex-1 text-center py-3 bg-red-50 border-2 border-red-200 rounded-lg" data-testid="full-message">
                <p className="font-medium text-red-700">This opportunity is full</p>
                <p className="text-sm text-red-600 mt-1">No more spots available</p>
              </div>
            )}
            {userSignupStatus === 'pending' && (
              <div className="flex-1">
                <div className="text-center py-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg" data-testid="pending-message">
                  <p className="font-medium text-yellow-800">Request Pending Approval</p>
                  <p className="text-sm text-yellow-700 mt-1">You'll be notified once it's approved</p>
                </div>
                <button
                  onClick={handleCancel}
                  className="btn-outline w-full mt-3"
                  disabled={actionLoading}
                  data-testid="cancel-pending-button"
                >
                  {actionLoading ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </div>
            )}
            {userSignupStatus === 'approved' && !isPastDate && (
              <button
                onClick={handleCancel}
                className="btn-outline flex-1"
                disabled={actionLoading}
                data-testid="cancel-signup-button"
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Signup'}
              </button>
            )}
            {userSignupStatus === 'approved' && isPastDate && (
              <button
                onClick={handleRequestHours}
                className="btn-primary flex-1"
                disabled={actionLoading}
                data-testid="request-hours-button"
              >
                {actionLoading ? 'Requesting...' : 'Request Credit Hours'}
              </button>
            )}
            {userSignupStatus === 'declined' && (
              <div className="flex-1 text-center py-3 bg-red-50 border-2 border-red-300 rounded-lg text-red-800" data-testid="declined-message">
                Your request was declined
              </div>
            )}
            {isFull && !opportunity.user_signed_up && (
              <div className="flex-1 text-center py-3 text-[#666666]" data-testid="full-message">
                This opportunity is currently full
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetails;